<template>
  <v-timeline side="end" density="compact" align="start" truncate-line="both" class="ms-flow">
    <!-- helpers -->
    <v-timeline-item v-for="(h, i) in helpers" :key="'h' + i" dot-color="surface-variant" size="x-small">
      <div class="d-flex flex-column ga-2">
        <div class="d-flex align-center flex-wrap ga-2">
          <span class="text-body-1 text-medium-emphasis">Ask</span>
          <ChipSelect
            :model-value="h.agent"
            :options="agentOptions(h.agent)"
            icon="mdi-robot-outline"
            @update:model-value="(v) => patchHelper(i, { agent: v })"
          />
          <span class="text-body-1 text-medium-emphasis">to read</span>
          <ChipSelect
            :model-value="h.input"
            :options="inputOptions"
            icon="mdi-database-outline"
            @update:model-value="(v) => patchHelper(i, { input: v })"
          />
          <v-spacer />
          <Button icon="mdi-trash-can-outline" tooltip="Remove helper" size="small" :click="() => removeHelper(i)" />
        </div>
        <v-text-field
          :model-value="h.role"
          variant="plain"
          density="compact"
          hide-details
          class="ms-flow__role font-italic"
          @update:model-value="(v) => patchHelper(i, { role: v })"
        />
      </div>
    </v-timeline-item>

    <!-- add helper -->
    <v-timeline-item dot-color="surface" size="x-small">
      <v-btn variant="text" size="small" color="primary" prepend-icon="mdi-plus" @click="$emit('add-helper')">
        Add a helper
      </v-btn>
    </v-timeline-item>

    <!-- lead -->
    <v-timeline-item dot-color="primary" size="small" icon="mdi-star-four-points">
      <div class="d-flex align-center flex-wrap ga-2">
        <ChipSelect
          :model-value="lead"
          :options="agentOptions(lead)"
          icon="mdi-robot"
          accent
          @update:model-value="(v) => $emit('update:lead', v)"
        />
        <span class="text-body-1 text-medium-emphasis">combines their work into the final result.</span>
      </div>
    </v-timeline-item>
  </v-timeline>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import ChipSelect from './ChipSelect.vue'
import Button from '@/components/sub/Button.vue'
import type { AutoHelper, AutoInput, ChipOption } from '@/types/automations'

@Options({
  components: { ChipSelect, Button },
  props: {
    helpers: { type: Array, default: () => [] },
    lead: { type: String, default: '' },
    agents: { type: Array, default: () => [] },
    inputs: { type: Array, default: () => [] }
  },
  emits: ['update:helpers', 'update:lead', 'add-helper', 'edit-helper', 'remove-helper']
})
export default class WorkflowFlow extends Vue {
  helpers!: AutoHelper[]
  lead!: string
  agents!: string[]
  inputs!: AutoInput[]

  get inputOptions(): ChipOption[] {
    return this.inputs.length ? this.inputs.map((i) => i.key) : ['(no inputs yet)']
  }

  agentOptions(current: string): ChipOption[] {
    return this.agents.includes(current) ? this.agents : [current, ...this.agents]
  }

  // Structural chip changes (agent/input) are emitted as explicit intents — the parent rewrites the
  // matching prompt token for exactly this chip, never by diffing arrays (a misfired diff once
  // deleted real helper tokens). Role-only changes stay on update:helpers: roles are session-only
  // display text with no home in the prompt, so they must not trigger a token rewrite.
  patchHelper(i: number, patch: Partial<AutoHelper>) {
    const current = this.helpers[i]
    if (!current) return
    const next = { ...current, ...patch }
    if (next.agent !== current.agent || next.input !== current.input) {
      this.$emit('edit-helper', {
        index: i,
        from: { agent: current.agent, input: current.input },
        to: { agent: next.agent, input: next.input }
      })
      return
    }
    this.$emit(
      'update:helpers',
      this.helpers.map((h, j) => (j === i ? next : h))
    )
  }

  removeHelper(i: number) {
    const current = this.helpers[i]
    if (!current) return
    this.$emit('remove-helper', { index: i, from: { agent: current.agent, input: current.input } })
  }
}
</script>

<style scoped lang="scss">
.ms-flow__role {
  border-bottom: 1px solid rgb(var(--v-theme-border-color));
}

// Overflow guard: timeline rows shrink with their container instead of pushing it wider.
.ms-flow {
  :deep(.v-timeline-item__body) {
    min-width: 0;
    width: 100%;
  }
}
</style>
